import { CheckCircle } from 'lucide-react'
import React from 'react'
import { FIELD_KEYS, getFieldValue } from '../../config/columns'
import type { StrategyResult } from '../../lib/analysis-utils'
import { TABLE_CLASSES } from '../../lib/table-utils'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
// Ghost Components and Styles
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { TableSkeleton } from '../ui/TableSkeleton'

import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'

interface MultiStrategyTableProps {
    strategies: StrategyResult[]
    activeStrategyId: string | null
    onSelectStrategy: (id: string) => void
    isLoading: boolean
    supportStrike?: number
    resistanceStrike?: number
}

export const MultiStrategyTable: React.FC<MultiStrategyTableProps> = (props) => (
    <WidgetErrorBoundary fallbackDescription="Strategy Matrix">
        <MultiStrategyTableContent {...props} />
    </WidgetErrorBoundary>
)

const MultiStrategyTableContent: React.FC<MultiStrategyTableProps> = ({
    strategies,
    activeStrategyId,
    onSelectStrategy,
    isLoading,
}) => {
    const deferredStrategies = React.useDeferredValue(strategies)
    if (isLoading) {
        return <TableSkeleton rows={3} columns={8} showSummary={false} />
    }

    if (strategies.length === 0) {
        return (
            <div className="p-8 text-center text-slate-400 dark:text-slate-500 italic">
                Select strikes to generate strategy analysis.
            </div>
        )
    }

    return (
        <TableWrapper className="shadow-xl border border-white/40 dark:border-slate-800">
            <div className={TABLE_CLASSES.CONTAINER}>
                <table className="w-full border-separate border-spacing-0">
                    <thead className="sticky top-0 z-30">
                        <tr>
                            <GhostTh align="left" side="neutral" className="top-0!">
                                Strategy
                            </GhostTh>
                            <GhostTh align="right" side="neutral" className="top-0!">
                                Net Premium
                            </GhostTh>
                            <GhostTh align="right" side="put" className="top-0!">
                                Max Profit
                            </GhostTh>
                            <GhostTh align="right" side="call" className="top-0!">
                                Max Loss
                            </GhostTh>
                            <GhostTh align="center" side="neutral" className="top-0!">
                                Breakeven Range
                            </GhostTh>
                            <GhostTh align="right" side="neutral" className="top-0!">
                                PoP %
                            </GhostTh>
                            <GhostTh align="center" side="neutral" className="top-0!">
                                R:R
                            </GhostTh>
                            <GhostTh align="right" side="neutral" className="top-0!">
                                Action
                            </GhostTh>
                        </tr>
                    </thead>
                    <tbody className="bg-white/30 dark:bg-slate-900/30 divide-y divide-slate-100/50 dark:divide-slate-800/50">
                        {deferredStrategies.map((strategy) => {
                            const name = getFieldValue(strategy, FIELD_KEYS.STRATEGY_NAME)
                            const type = getFieldValue(strategy, FIELD_KEYS.STRATEGY_TYPE)
                            const netPremium = getFieldValue(strategy, FIELD_KEYS.NET_PREMIUM) ?? 0
                            const maxProfit = getFieldValue(strategy, FIELD_KEYS.MAX_PROFIT)
                            const maxLoss = getFieldValue(strategy, FIELD_KEYS.MAX_LOSS) ?? 0
                            const beLower = getFieldValue(strategy, FIELD_KEYS.BREAKEVEN_LOWER) ?? 0
                            const beUpper = getFieldValue(strategy, FIELD_KEYS.BREAKEVEN_UPPER) ?? 0
                            const pop = getFieldValue(strategy, FIELD_KEYS.POP) ?? 0
                            const rr = getFieldValue(strategy, FIELD_KEYS.RISK_REWARD) ?? 0
                            const isActive = activeStrategyId === strategy.id

                            const tdClass =
                                'p-2 align-middle bg-white/40 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 transition-colors h-[60px]'

                            return (
                                <tr
                                    key={strategy.id}
                                    onClick={() => onSelectStrategy(strategy.id)}
                                    className={`cursor-pointer group hover:z-20 relative ${isActive ? 'bg-indigo-50/60 dark:bg-indigo-900/40 shadow-inner' : ''}`}
                                >
                                    <td className={`${tdClass} text-left pl-4`}>
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`w-1 h-8 rounded-full transition-all duration-300 ${isActive ? 'bg-indigo-500 scale-y-100' : 'bg-slate-300 scale-y-50 group-hover:bg-indigo-300 group-hover:scale-y-75'}`}
                                            ></div>
                                            <div>
                                                <div className="font-bold text-sm text-slate-800 dark:text-slate-200">
                                                    {name}
                                                </div>
                                                <div className="text-[9px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-wider">
                                                    {type}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={`${tdClass} text-right`}>
                                        <NumberCell
                                            value={typeof netPremium === 'number' ? netPremium.toFixed(2) : '-'}
                                            className="text-slate-700 dark:text-slate-300 font-medium"
                                        />
                                    </td>
                                    <td className={`${tdClass} text-right`}>
                                        <NumberCell
                                            value={
                                                maxProfit === Infinity
                                                    ? 'Uncapped'
                                                    : typeof maxProfit === 'number'
                                                      ? maxProfit.toFixed(2)
                                                      : '-'
                                            }
                                            className="text-emerald-600 font-bold"
                                        />
                                    </td>
                                    <td className={`${tdClass} text-right`}>
                                        <NumberCell
                                            value={typeof maxLoss === 'number' ? maxLoss.toFixed(2) : '-'}
                                            className="text-rose-600 font-bold"
                                        />
                                    </td>
                                    <td className={`${tdClass} text-center`}>
                                        <span className="bg-slate-100/80 dark:bg-slate-800/80 px-2 py-1 rounded text-xs font-bold text-slate-600 dark:text-slate-400 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
                                            {typeof beLower === 'number' ? beLower.toFixed(0) : '?'} -{' '}
                                            {typeof beUpper === 'number' ? beUpper.toFixed(0) : '?'}
                                        </span>
                                    </td>
                                    <td className={`${tdClass} text-right`}>
                                        <NumberCell
                                            value={(pop * 100).toFixed(0)}
                                            suffix="%"
                                            className={
                                                pop > 0.5 ? 'text-emerald-600 font-black' : 'text-amber-600 font-black'
                                            }
                                        />
                                    </td>
                                    <td className={`${tdClass} text-center`}>
                                        <NumberCell
                                            value={typeof rr === 'number' ? rr.toFixed(2) : '-'}
                                            className="text-slate-500 dark:text-slate-400 text-xs"
                                        />
                                    </td>
                                    <td className={`${tdClass} text-right pr-6`}>
                                        {isActive && (
                                            <CheckCircle
                                                size={18}
                                                className="text-indigo-500 ml-auto animate-in zoom-in spin-in-90 duration-300"
                                            />
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </TableWrapper>
    )
}
